import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, User, Heart, FileText, Users as UsersIcon, Briefcase, Plus, Trash2, Camera, ChevronDown, X } from 'lucide-react';
import Avatar from '../components/Avatar';
import { PrimaryButton } from '../components/Button';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { AVATAR_COLORS, AVATAR_COLOR_LIST, FAMILY_ROLES, DOC_TYPES, CONTACT_CATEGORIES, BLOOD_TYPES } from '../lib/family';

const TABS = [
    { key: 'personal', label: 'Personal', icon: User },
    { key: 'salud', label: 'Salud', icon: Heart },
    { key: 'documentos', label: 'Docs', icon: FileText },
    { key: 'contactos', label: 'Contactos', icon: UsersIcon },
    { key: 'laboral', label: 'Laboral', icon: Briefcase },
];

const emptyMember = () => ({
    profile_type: 'informative',
    fill_mode: 'self',
    invite_email: '',
    avatar_color: 'pink',
    avatar_url: null,
    family_role_label: '',
    custom_role: '',
    personal: { full_name: '', birthdate: '', id_number: '', phone: '', email: '', address: '' },
    health: { blood_type: '', allergies: [], medications: [], doctors: [], insurance: { company: '', policy_number: '', coverage: '' } },
    documents: [],
    contacts: [],
    work: { company: '', position: '', supervisor: '', work_phone: '', work_email: '', office_address: '' },
    field_privacy: {},
});

export default function MemberForm({ mode = 'create' }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { memberId } = useParams();
    const [params, setParams] = useSearchParams();
    const fileRef = useRef(null);

    const [step, setStep] = useState(mode === 'create' ? 'choose' : 'form'); // choose | fill-mode | form
    const [tab, setTab] = useState(params.get('tab') || 'personal');
    const [form, setForm] = useState(emptyMember());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (mode === 'edit' && memberId) {
            api.get(`/family/members/${memberId}`).then((r) => {
                const m = r.data.member;
                setForm({
                    profile_type: m.profile_type || 'informative',
                    fill_mode: 'self',
                    invite_email: m.email || '',
                    avatar_color: m.avatar_color || 'pink',
                    avatar_url: m.avatar_url,
                    family_role_label: FAMILY_ROLES.includes(m.family_role_label) ? m.family_role_label : (m.family_role_label ? 'Otro' : ''),
                    custom_role: !FAMILY_ROLES.includes(m.family_role_label) ? (m.family_role_label || '') : '',
                    personal: m.personal || emptyMember().personal,
                    health: m.health || emptyMember().health,
                    documents: m.documents || [],
                    contacts: m.contacts || [],
                    work: m.work || emptyMember().work,
                    field_privacy: m.field_privacy || {},
                });
            }).catch((e) => setError(formatApiError(e)));
        }
    }, [mode, memberId]);

    const finalRoleLabel = form.family_role_label === 'Otro' ? form.custom_role : form.family_role_label;

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setForm((f) => ({ ...f, avatar_url: reader.result }));
        reader.readAsDataURL(file);
    };

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...form,
                family_role_label: finalRoleLabel || null,
                invite_email: form.invite_email || undefined,
            };
            delete payload.custom_role;

            if (mode === 'create') {
                const { data } = await api.post('/family/members', payload);
                if (data.invitation_sent_to) {
                    alert(`Invitación enviada a ${data.invitation_sent_to}.`);
                } else if (data.member) {
                    // Created informative
                }
                navigate('/familia');
            } else {
                await api.put(`/family/members/${memberId}`, {
                    avatar_color: payload.avatar_color,
                    avatar_url: payload.avatar_url,
                    family_role_label: payload.family_role_label,
                    personal: payload.personal,
                    health: payload.health,
                    documents: payload.documents,
                    contacts: payload.contacts,
                    work: payload.work,
                    field_privacy: payload.field_privacy,
                });
                navigate(`/familia/miembro/${memberId}`);
            }
        } catch (e) {
            setError(formatApiError(e));
        } finally { setSaving(false); }
    };

    // STEP 1: Choose profile type (create only)
    if (step === 'choose' && mode === 'create') {
        return (
            <WizardScreen title="Nuevo Miembro" onBack={() => navigate('/familia')}>
                <p className="text-gray-500 text-sm mb-6">Elige el tipo de perfil que quieres crear:</p>

                <button
                    onClick={() => { setForm({ ...form, profile_type: 'active' }); setStep('fill-mode'); }}
                    className="w-full bg-white border-2 border-transparent hover:border-brand rounded-2xl p-5 mb-3 text-left shadow-card transition active:scale-[0.98]"
                    data-testid="profile-type-active"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-brand/10 flex items-center justify-center"><User className="w-7 h-7 text-brand" /></div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-brand-navy">Perfil Activo</h3>
                            <p className="text-xs text-gray-500 mt-1">Usuario con cuenta y acceso propio a la app</p>
                        </div>
                    </div>
                </button>

                <button
                    onClick={() => { setForm({ ...form, profile_type: 'informative', fill_mode: 'self' }); setStep('form'); }}
                    className="w-full bg-white border-2 border-transparent hover:border-brand rounded-2xl p-5 text-left shadow-card transition active:scale-[0.98]"
                    data-testid="profile-type-informative"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center"><UsersIcon className="w-7 h-7 text-emerald-500" /></div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-brand-navy">Perfil Informativo</h3>
                            <p className="text-xs text-gray-500 mt-1">Solo registro de datos (bebé, abuelos, etc.). Sin login.</p>
                        </div>
                    </div>
                    <p className="mt-3 text-[11px] text-gray-400">Podrás transferir el control en el futuro.</p>
                </button>
            </WizardScreen>
        );
    }

    // STEP 2: Who fills (active only)
    if (step === 'fill-mode' && mode === 'create') {
        return (
            <WizardScreen title="¿Quién llenará el perfil?" onBack={() => setStep('choose')}>
                <p className="text-gray-500 text-sm mb-6">Tu eliges si llenas los datos o si envías una invitación para que la persona los llene.</p>

                <button
                    onClick={() => { setForm({ ...form, fill_mode: 'self' }); setStep('form'); }}
                    className="w-full bg-white border-2 border-transparent hover:border-brand rounded-2xl p-5 mb-3 text-left shadow-card transition active:scale-[0.98]"
                    data-testid="fill-mode-self"
                >
                    <h3 className="text-base font-bold text-brand-navy">Yo lo llenaré</h3>
                    <p className="text-xs text-gray-500 mt-1">Cargas los datos ahora, después invitas para que confirme.</p>
                </button>

                <button
                    onClick={() => { setForm({ ...form, fill_mode: 'invite' }); setStep('form'); }}
                    className="w-full bg-white border-2 border-transparent hover:border-brand rounded-2xl p-5 text-left shadow-card transition active:scale-[0.98]"
                    data-testid="fill-mode-invite"
                >
                    <h3 className="text-base font-bold text-brand-navy">Enviar invitación</h3>
                    <p className="text-xs text-gray-500 mt-1">La persona recibe email y llena sus propios datos.</p>
                </button>
            </WizardScreen>
        );
    }

    // STEP 3: Full form
    const tabContent = {
        personal: <PersonalForm form={form} setForm={setForm} />,
        salud: <HealthForm form={form} setForm={setForm} />,
        documentos: <DocsForm form={form} setForm={setForm} />,
        contactos: <ContactsForm form={form} setForm={setForm} />,
        laboral: <WorkForm form={form} setForm={setForm} />,
    };

    return (
        <div className="min-h-screen bg-surface app-shell pb-32">
            <div className="px-4 pt-4 flex items-center justify-between">
                <button onClick={() => mode === 'create' ? setStep(form.profile_type === 'active' ? 'fill-mode' : 'choose') : navigate(-1)} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="member-form-back">
                    <ArrowLeft className="w-5 h-5 text-brand-navy" />
                </button>
                <h1 className="text-base font-bold text-brand-navy">{mode === 'create' ? 'Nuevo Miembro' : 'Editar Miembro'}</h1>
                <button onClick={save} disabled={saving} className="px-4 h-9 bg-brand text-white rounded-full text-sm font-bold disabled:opacity-50" data-testid="member-form-save">
                    {saving ? '...' : 'Guardar'}
                </button>
            </div>

            {/* Avatar + role */}
            <div className="px-4 mt-4 flex flex-col items-center">
                <button onClick={() => fileRef.current?.click()} className="relative" data-testid="member-form-avatar-btn">
                    <Avatar name={form.personal.full_name || 'Nuevo'} color={form.avatar_color} photo={form.avatar_url} size={88} ring />
                    <span className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-brand text-white flex items-center justify-center"><Camera className="w-3.5 h-3.5" /></span>
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
                <div className="flex gap-2 mt-3" data-testid="member-form-color-picker">
                    {AVATAR_COLOR_LIST.map((c) => (
                        <button key={c} onClick={() => setForm({ ...form, avatar_color: c })} className={`w-7 h-7 rounded-full transition ${form.avatar_color === c ? 'ring-2 ring-offset-2 ring-brand scale-110' : 'opacity-70 hover:opacity-100'}`} style={{ backgroundColor: AVATAR_COLORS[c].border }} aria-label={c} data-testid={`color-${c}`} />
                    ))}
                </div>
            </div>

            {/* Tabs */}
            <div className="px-4 mt-5 mb-4">
                <div className="bg-white shadow-card rounded-full p-1 flex">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`flex-1 h-10 rounded-full text-[11px] font-bold flex items-center justify-center gap-1 transition ${tab === t.key ? 'bg-brand text-white' : 'text-gray-500'}`}
                            data-testid={`form-tab-${t.key}`}
                        >
                            <t.icon className="w-3.5 h-3.5" />
                        </button>
                    ))}
                </div>
            </div>

            <div className="px-4">
                {tabContent[tab]}
            </div>

            {error && <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700" data-testid="member-form-error">{error}</div>}

            <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] bg-white border-t border-gray-100 p-4">
                <PrimaryButton onClick={save} loading={saving} testId="member-form-save-bottom">{mode === 'create' ? 'Guardar miembro' : 'Guardar cambios'}</PrimaryButton>
            </div>
        </div>
    );
}

function WizardScreen({ title, onBack, children }) {
    return (
        <div className="min-h-screen bg-surface app-shell px-6 py-6">
            <div className="flex items-center justify-between mb-6">
                <button onClick={onBack} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="wizard-back">
                    <ArrowLeft className="w-5 h-5 text-brand-navy" />
                </button>
                <h1 className="text-lg font-bold text-brand-navy">{title}</h1>
                <div className="w-10" />
            </div>
            {children}
        </div>
    );
}

function FormCard({ title, action, children, testId }) {
    return (
        <div className="bg-white rounded-2xl shadow-card p-4 mb-4" data-testid={testId}>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-brand-navy">{title}</h3>
                {action}
            </div>
            {children}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="mb-3 last:mb-0">
            <label className="block text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-1">{label}</label>
            {children}
        </div>
    );
}

function Input({ value, onChange, placeholder, type = 'text', testId, className = '' }) {
    return (
        <input
            type={type}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full h-11 bg-white border border-gray-200 rounded-xl px-3 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 ${className}`}
            data-testid={testId}
        />
    );
}

function Select({ value, onChange, options, placeholder = 'Selecciona...', testId, className = '' }) {
    return (
        <div className={`relative ${className}`}>
            <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full h-11 bg-white border border-gray-200 rounded-xl px-3 pr-9 text-sm appearance-none focus:border-brand focus:ring-2 focus:ring-brand/20" data-testid={testId}>
                <option value="">{placeholder}</option>
                {options.map((o) => (
                    <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
                        {typeof o === 'string' ? o : o.label}
                    </option>
                ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
        </div>
    );
}

function PersonalForm({ form, setForm }) {
    const upd = (k, v) => setForm({ ...form, personal: { ...form.personal, [k]: v } });
    return (
        <>
            <FormCard title="Datos Personales" testId="form-personal">
                {form.profile_type === 'active' && form.fill_mode === 'invite' && (
                    <Field label="Correo de invitación *">
                        <Input value={form.invite_email} onChange={(v) => setForm({ ...form, invite_email: v })} placeholder="correo@ejemplo.com" type="email" testId="form-invite-email" />
                    </Field>
                )}
                <Field label="Nombre completo">
                    <Input value={form.personal.full_name} onChange={(v) => upd('full_name', v)} placeholder="Ej. María García" testId="form-full-name" />
                </Field>
                <Field label="Rol en la familia">
                    <Select value={form.family_role_label} onChange={(v) => setForm({ ...form, family_role_label: v })} options={FAMILY_ROLES} placeholder="Selecciona..." testId="form-role-label" />
                </Field>
                {form.family_role_label === 'Otro' && (
                    <Field label="Especifica el rol">
                        <Input value={form.custom_role} onChange={(v) => setForm({ ...form, custom_role: v })} placeholder="Ej. Madrina, Vecino, etc." testId="form-custom-role" />
                    </Field>
                )}
                <Field label="Fecha de nacimiento">
                    <Input value={form.personal.birthdate} onChange={(v) => upd('birthdate', v)} type="date" testId="form-birthdate" />
                </Field>
                <Field label="Número de identidad">
                    <Input value={form.personal.id_number} onChange={(v) => upd('id_number', v)} placeholder="0000-00000-0000" testId="form-id-number" />
                </Field>
                <Field label="Teléfono">
                    <Input value={form.personal.phone} onChange={(v) => upd('phone', v)} placeholder="+502 0000-0000" type="tel" testId="form-phone" />
                </Field>
                <Field label="Correo electrónico">
                    <Input value={form.personal.email} onChange={(v) => upd('email', v)} placeholder="correo@ejemplo.com" type="email" testId="form-email" />
                </Field>
                <Field label="Dirección">
                    <Input value={form.personal.address} onChange={(v) => upd('address', v)} placeholder="Calle, ciudad, país" testId="form-address" />
                </Field>
            </FormCard>
        </>
    );
}

function HealthForm({ form, setForm }) {
    const h = form.health;
    const setH = (patch) => setForm({ ...form, health: { ...h, ...patch } });
    const newId = () => Math.random().toString(36).slice(2);

    return (
        <>
            <FormCard title="Tipo de sangre" testId="form-health-blood">
                <Select value={h.blood_type} onChange={(v) => setH({ blood_type: v })} options={BLOOD_TYPES} testId="form-blood-type" />
            </FormCard>

            <FormCard
                title="Alergias"
                testId="form-health-allergies"
                action={<AddBtn onClick={() => setH({ allergies: [...h.allergies, { id: newId(), name: '' }] })} testId="add-allergy" />}
            >
                {h.allergies.map((a, i) => (
                    <div key={a.id} className="flex items-center gap-2 mb-2">
                        <Input value={a.name} onChange={(v) => { const arr = [...h.allergies]; arr[i] = { ...a, name: v }; setH({ allergies: arr }); }} placeholder="Ej. Penicilina" testId={`allergy-input-${i}`} />
                        <DelBtn onClick={() => setH({ allergies: h.allergies.filter((_, idx) => idx !== i) })} testId={`allergy-del-${i}`} />
                    </div>
                ))}
                {!h.allergies.length && <p className="text-xs text-gray-400 italic">Sin alergias</p>}
            </FormCard>

            <FormCard
                title="Medicamentos"
                testId="form-health-medications"
                action={<AddBtn onClick={() => setH({ medications: [...h.medications, { id: newId(), name: '', dose: '' }] })} testId="add-medication" />}
            >
                {h.medications.map((m, i) => (
                    <div key={m.id} className="p-3 bg-gray-50 rounded-xl mb-2">
                        <div className="flex items-start gap-2">
                            <div className="flex-1 space-y-2">
                                <Input value={m.name} onChange={(v) => { const arr = [...h.medications]; arr[i] = { ...m, name: v }; setH({ medications: arr }); }} placeholder="Nombre" testId={`med-name-${i}`} />
                                <Input value={m.dose} onChange={(v) => { const arr = [...h.medications]; arr[i] = { ...m, dose: v }; setH({ medications: arr }); }} placeholder="Dosis (ej. 10 mg / día)" testId={`med-dose-${i}`} />
                            </div>
                            <DelBtn onClick={() => setH({ medications: h.medications.filter((_, idx) => idx !== i) })} testId={`med-del-${i}`} />
                        </div>
                    </div>
                ))}
                {!h.medications.length && <p className="text-xs text-gray-400 italic">Sin medicamentos</p>}
            </FormCard>

            <FormCard
                title="Doctores"
                testId="form-health-doctors"
                action={<AddBtn onClick={() => setH({ doctors: [...h.doctors, { id: newId(), name: '', specialty: '', phone: '' }] })} testId="add-doctor" />}
            >
                {h.doctors.map((d, i) => (
                    <div key={d.id} className="p-3 bg-gray-50 rounded-xl mb-2">
                        <div className="flex items-start gap-2">
                            <div className="flex-1 space-y-2">
                                <Input value={d.name} onChange={(v) => { const arr = [...h.doctors]; arr[i] = { ...d, name: v }; setH({ doctors: arr }); }} placeholder="Nombre del doctor" testId={`doc-name-${i}`} />
                                <Input value={d.specialty} onChange={(v) => { const arr = [...h.doctors]; arr[i] = { ...d, specialty: v }; setH({ doctors: arr }); }} placeholder="Especialidad" testId={`doc-specialty-${i}`} />
                                <Input value={d.phone} onChange={(v) => { const arr = [...h.doctors]; arr[i] = { ...d, phone: v }; setH({ doctors: arr }); }} placeholder="Teléfono" type="tel" testId={`doc-phone-${i}`} />
                            </div>
                            <DelBtn onClick={() => setH({ doctors: h.doctors.filter((_, idx) => idx !== i) })} testId={`doc-del-${i}`} />
                        </div>
                    </div>
                ))}
                {!h.doctors.length && <p className="text-xs text-gray-400 italic">Sin doctores registrados</p>}
            </FormCard>

            <FormCard title="Seguro Médico" testId="form-health-insurance">
                <Field label="Compañía">
                    <Input value={h.insurance?.company} onChange={(v) => setH({ insurance: { ...h.insurance, company: v } })} placeholder="Ej. Seguros Atlántida" testId="ins-company" />
                </Field>
                <Field label="Número de póliza">
                    <Input value={h.insurance?.policy_number} onChange={(v) => setH({ insurance: { ...h.insurance, policy_number: v } })} placeholder="POL-0000-000" testId="ins-policy" />
                </Field>
                <Field label="Cobertura">
                    <Input value={h.insurance?.coverage} onChange={(v) => setH({ insurance: { ...h.insurance, coverage: v } })} placeholder="Ej. Familiar Premium" testId="ins-coverage" />
                </Field>
            </FormCard>
        </>
    );
}

function DocsForm({ form, setForm }) {
    const newId = () => Math.random().toString(36).slice(2);
    return (
        <FormCard
            title="Documentos"
            testId="form-documents"
            action={<AddBtn onClick={() => setForm({ ...form, documents: [...form.documents, { id: newId(), doc_type: 'identidad', name: '', number: '', expires_at: '' }] })} testId="add-document" />}
        >
            {form.documents.map((d, i) => (
                <div key={d.id} className="p-3 bg-gray-50 rounded-xl mb-2">
                    <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                            <Select value={d.doc_type} onChange={(v) => { const arr = [...form.documents]; arr[i] = { ...d, doc_type: v }; setForm({ ...form, documents: arr }); }} options={DOC_TYPES} testId={`doc-type-${i}`} />
                            <Input value={d.name} onChange={(v) => { const arr = [...form.documents]; arr[i] = { ...d, name: v }; setForm({ ...form, documents: arr }); }} placeholder="Nombre del documento" testId={`doc-name-${i}`} />
                            <Input value={d.number} onChange={(v) => { const arr = [...form.documents]; arr[i] = { ...d, number: v }; setForm({ ...form, documents: arr }); }} placeholder="Número" testId={`doc-number-${i}`} />
                            <Input value={d.expires_at} onChange={(v) => { const arr = [...form.documents]; arr[i] = { ...d, expires_at: v }; setForm({ ...form, documents: arr }); }} placeholder="Vencimiento" type="date" testId={`doc-expires-${i}`} />
                        </div>
                        <DelBtn onClick={() => setForm({ ...form, documents: form.documents.filter((_, idx) => idx !== i) })} testId={`doc-del-${i}`} />
                    </div>
                </div>
            ))}
            {!form.documents.length && <p className="text-xs text-gray-400 italic">Sin documentos</p>}
        </FormCard>
    );
}

function ContactsForm({ form, setForm }) {
    const newId = () => Math.random().toString(36).slice(2);
    return (
        <FormCard
            title="Contactos importantes"
            testId="form-contacts"
            action={<AddBtn onClick={() => setForm({ ...form, contacts: [...form.contacts, { id: newId(), category: 'familia', relation: '', name: '', phone: '' }] })} testId="add-contact" />}
        >
            {form.contacts.map((c, i) => (
                <div key={c.id} className="p-3 bg-gray-50 rounded-xl mb-2">
                    <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                            <Select value={c.category} onChange={(v) => { const arr = [...form.contacts]; arr[i] = { ...c, category: v }; setForm({ ...form, contacts: arr }); }} options={CONTACT_CATEGORIES} testId={`contact-cat-${i}`} />
                            <Input value={c.relation} onChange={(v) => { const arr = [...form.contacts]; arr[i] = { ...c, relation: v }; setForm({ ...form, contacts: arr }); }} placeholder="Relación (Madre, Jefe...)" testId={`contact-relation-${i}`} />
                            <Input value={c.name} onChange={(v) => { const arr = [...form.contacts]; arr[i] = { ...c, name: v }; setForm({ ...form, contacts: arr }); }} placeholder="Nombre" testId={`contact-name-${i}`} />
                            <Input value={c.phone} onChange={(v) => { const arr = [...form.contacts]; arr[i] = { ...c, phone: v }; setForm({ ...form, contacts: arr }); }} placeholder="Teléfono" type="tel" testId={`contact-phone-${i}`} />
                        </div>
                        <DelBtn onClick={() => setForm({ ...form, contacts: form.contacts.filter((_, idx) => idx !== i) })} testId={`contact-del-${i}`} />
                    </div>
                </div>
            ))}
            {!form.contacts.length && <p className="text-xs text-gray-400 italic">Sin contactos</p>}
        </FormCard>
    );
}

function WorkForm({ form, setForm }) {
    const w = form.work;
    const setW = (k, v) => setForm({ ...form, work: { ...w, [k]: v } });
    return (
        <FormCard title="Información Laboral" testId="form-work">
            <Field label="Empresa"><Input value={w.company} onChange={(v) => setW('company', v)} placeholder="Ej. TechStudio" testId="work-company" /></Field>
            <Field label="Puesto"><Input value={w.position} onChange={(v) => setW('position', v)} placeholder="Ej. Diseñador UX" testId="work-position" /></Field>
            <Field label="Jefe inmediato"><Input value={w.supervisor} onChange={(v) => setW('supervisor', v)} placeholder="Nombre del jefe" testId="work-supervisor" /></Field>
            <Field label="Teléfono de trabajo"><Input value={w.work_phone} onChange={(v) => setW('work_phone', v)} placeholder="+502 0000-0000" type="tel" testId="work-phone" /></Field>
            <Field label="Correo corporativo"><Input value={w.work_email} onChange={(v) => setW('work_email', v)} placeholder="trabajo@empresa.com" type="email" testId="work-email" /></Field>
            <Field label="Dirección de oficina"><Input value={w.office_address} onChange={(v) => setW('office_address', v)} placeholder="Edificio, zona" testId="work-address" /></Field>
        </FormCard>
    );
}

function AddBtn({ onClick, testId }) {
    return (
        <button onClick={onClick} type="button" className="flex items-center gap-1 text-xs font-bold text-brand hover:text-brand-dark" data-testid={testId}>
            <Plus className="w-4 h-4" /> Agregar
        </button>
    );
}

function DelBtn({ onClick, testId }) {
    return (
        <button onClick={onClick} type="button" className="w-9 h-9 bg-red-50 hover:bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0" data-testid={testId}>
            <Trash2 className="w-4 h-4 text-red-500" />
        </button>
    );
}
